import { useState, useEffect, useCallback } from 'react';
import { api } from '@expat-ui/shared';
import { formatCurrencyInput, extractNumericInput, formatAmount } from '../../utils/numberFormat';
import { getFreshAccessToken } from '../../utils/authUtils';
import { Goal, GoalCategory, Country, GOAL_CATEGORY_LABELS, COUNTRY_CURRENCIES } from '../../types/api.types';
import styles from './GoalsPage.module.scss';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

const GOAL_CATEGORIES: GoalCategory[] = ['REAL_ESTATE', 'EDUCATION', 'WEDDING', 'TRAVEL', 'VEHICLE', 'OTHER'];

export function GoalsPage() {
    const [goals, setGoals] = useState<Goal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);

    const fetchGoals = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const data = await api.get<Goal[]>('/goals', token);
            setGoals(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load goals');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGoals();
    }, [fetchGoals]);

    const deleteGoal = async (goalId: string) => {
        const token = await getFreshAccessToken();
        if (!token) return;
        await api.delete(`/goals/${goalId}`, token);
        fetchGoals();
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading goals...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={fetchGoals}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Goals</h1>
                    <p className={styles.subtitle}>Define milestone goals like buying property or funding education.</p>
                </div>
                <button className={styles.addButton} onClick={() => setShowForm(true)}>
                    + Add Goal
                </button>
            </div>

            <div className={styles.helpNote}>
                <p className={styles.helpNoteTitle}>How goals work in the simulator</p>
                <ul className={styles.helpNoteList}>
                    <li>
                        Each goal is withdrawn from your portfolio at your target age in every simulation — it is real
                        money out, not just a tracker.
                    </li>
                    <li>
                        The simulator shows a <strong>Goal Outcomes</strong> section with the % of scenarios where your
                        portfolio had enough to fund each goal.
                    </li>
                    <li>
                        If you cannot afford a goal, the simulator drains whatever remains and continues — a severe
                        shortfall can cascade into portfolio failure for that simulation.
                    </li>
                    <li>
                        <strong>Don't duplicate:</strong> if you've added the same event as a Retirement one-time
                        expense, remove one. Both are deducted independently.
                    </li>
                </ul>
            </div>

            {goals.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No goals yet. Set a financial milestone to track.</p>
                    <button className={styles.addButton} onClick={() => setShowForm(true)}>
                        + Add your first goal
                    </button>
                </div>
            ) : (
                <div className={styles.goalList}>
                    {goals.map((goal) => (
                        <GoalCard key={goal.goalId} goal={goal} onDelete={deleteGoal} />
                    ))}
                </div>
            )}

            {showForm && (
                <AddGoalModal
                    onClose={() => setShowForm(false)}
                    onAdded={() => {
                        setShowForm(false);
                        fetchGoals();
                    }}
                />
            )}
        </div>
    );
}

function GoalCard({ goal, onDelete }: { goal: Goal; onDelete: (id: string) => void }) {
    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardCategory}>{GOAL_CATEGORY_LABELS[goal.category]}</span>
                <span className={styles.cardCountry}>{goal.country}</span>
            </div>
            <div className={styles.cardBody}>
                <span className={styles.cardLabel}>{goal.label}</span>
                <span className={styles.cardAmount}>{formatAmount(goal.targetAmount, goal.currency)}</span>
                <span className={styles.cardMeta}>Target Age: {goal.targetAge}</span>
            </div>
            <button className={styles.deleteButton} onClick={() => onDelete(goal.goalId)}>
                Remove
            </button>
        </div>
    );
}

interface AddGoalModalProps {
    onClose: () => void;
    onAdded: () => void;
}

function AddGoalModal({ onClose, onAdded }: AddGoalModalProps) {
    const [label, setLabel] = useState('');
    const [category, setCategory] = useState<GoalCategory>('REAL_ESTATE');
    const [country, setCountry] = useState<Country>('INDIA');
    const currency = COUNTRY_CURRENCIES[country];
    const [targetAmount, setTargetAmount] = useState('');
    const [targetAge, setTargetAge] = useState('50');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            await api.post(
                '/goals',
                {
                    label,
                    category,
                    country,
                    targetAmount: Number(targetAmount),
                    currency,
                    targetAge: Number(targetAge),
                },
                token,
            );
            onAdded();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Add Goal</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Goal Name</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g., Buy apartment in Hyderabad"
                            required
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as GoalCategory)}
                        >
                            {GOAL_CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                    {GOAL_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Target Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(targetAmount, currency)}
                                onChange={(e) => setTargetAmount(extractNumericInput(e.target.value))}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Target Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={targetAge}
                                onChange={(e) => setTargetAge(e.target.value)}
                                min={25}
                                max={95}
                                required
                            />
                        </div>
                    </div>

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Add Goal'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
